import React, { useState, useRef, useEffect } from 'react';
import { CALENDLY_URL } from '../../config';
import { IcoClose } from './DashIcons';

const QUICK_REPLIES = [
  'I found a bug',
  'Payroll calculation is wrong',
  'I cannot log in',
  'How do I add an employee?',
  'Book a Demo',
];

function priorityStyle(p) {
  if (p === 'urgent') return { bg: '#FCEBEB', color: '#791F1F', label: 'Urgent' };
  if (p === 'low')    return { bg: '#E1F5EE', color: '#085041', label: 'Low' };
  return { bg: '#E6F1FB', color: '#0C447C', label: 'Normal' };
}

export default function SupportWidget() {
  const [open, setOpen]             = useState(false);
  const [tab, setTab]               = useState('chat');
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [tickets, setTickets]       = useState([]);
  const [hasNew, setHasNew]         = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const messagesEnd = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setTimeout(() => {
        setMessages([{
          role: 'assistant',
          content: "Hi! 👋 I'm the ClockEarnings support assistant. I can help troubleshoot issues, create a support ticket, or book you a demo. What's going on?",
          time: now(),
        }]);
      }, 300);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  function now() {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  const handleSend = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    // "Book a Demo" opens Calendly directly
    if (msg === 'Book a Demo') {
      setShowCalendly(true);
      return;
    }

    setInput('');

    const userMsg = { role: 'user', content: msg, time: now() };
    const next    = [...messages, userMsg];
    setMessages(next);
    setLoading(true);

    try {
      const res  = await fetch('/api/chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          messages: next.map(m => ({ role: m.role, content: m.content })),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Server error');

      if (data.ticket) {
        setTickets(prev => [data.ticket, ...prev]);
        setHasNew(true);
      }

      setMessages(prev => [...prev, {
        role:    'assistant',
        content: data.reply,
        time:    now(),
        ticket:  data.ticket || null,
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role:    'assistant',
        content: "Sorry, I couldn't connect right now. Please try again or email contact@flurrysystems.com directly.",
        time:    now(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>

      {/* Calendly modal */}
      {showCalendly && (
        <div
          onClick={() => setShowCalendly(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 900, height: '90vh', background: '#fff', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem', color: '#111827' }}>Book a Demo</p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>Pick a time and we'll walk you through ClockEarnings live.</p>
              </div>
              <button onClick={() => setShowCalendly(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center' }}><IcoClose size={16}/></button>
            </div>
            <iframe
              src={`${CALENDLY_URL}?embed_type=Inline&hide_gdpr_banner=1`}
              title="Book a Demo"
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </div>
        </div>
      )}

      {/* Launcher */}
      <button
        onClick={() => { setOpen(o => !o); setHasNew(false); }}
        style={{
          position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8,
          background: '#1D9E75', color: 'white', border: 'none', borderRadius: 24,
          padding: '10px 18px 10px 14px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(29,158,117,0.4)',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Support
        {hasNew && (
          <span style={{
            position: 'absolute', top: 6, right: 6,
            width: 8, height: 8, background: '#E24B4A', borderRadius: '50%',
          }} />
        )}
      </button>

      {/* Window */}
      {open && (
        <div style={{
          marginTop: 12, width: 360, height: 520,
          display: 'flex', flexDirection: 'column',
          border: '0.5px solid #e0e0e0', borderRadius: 12,
          overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', background: 'white',
          animation: 'slideUp 0.2s ease',
        }}>
          {/* Header */}
          <div style={{
            background: '#0F6E56', color: 'white', padding: '12px 16px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600,
            }}>CE</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>ClockEarnings Support</div>
              <div style={{ fontSize: 11, opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, background: '#9FE1CB', borderRadius: '50%', display: 'inline-block' }} />
                Online · usually replies instantly
              </div>
            </div>
            <button
              onClick={() => setShowCalendly(true)}
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8, whiteSpace: 'nowrap' }}
            >Book Demo</button>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: 'white', opacity: 0.7, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            ><IcoClose size={16}/></button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '0.5px solid #e8e8e8' }}>
            {['chat', 'tickets'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  flex: 1, padding: '10px 0', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', border: 'none', background: 'none',
                  color: tab === t ? '#0F6E56' : '#888',
                  borderBottom: tab === t ? '2px solid #0F6E56' : '2px solid transparent',
                  textTransform: 'capitalize',
                }}
              >
                {t}
                {t === 'tickets' && tickets.length > 0 && (
                  <span style={{
                    marginLeft: 6, background: '#E1F5EE', color: '#0F6E56',
                    borderRadius: 10, padding: '1px 6px', fontSize: 11,
                  }}>{tickets.length}</span>
                )}
              </button>
            ))}
          </div>

          {/* Chat */}
          {tab === 'chat' && (
            <>
              <div style={{
                flex: 1, overflowY: 'auto', padding: 14,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                {messages.map((m, i) => (
                  <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                    <div style={{
                      padding: '9px 12px', borderRadius: 12,
                      borderBottomLeftRadius:  m.role === 'assistant' ? 4 : 12,
                      borderBottomRightRadius: m.role === 'user'      ? 4 : 12,
                      background: m.role === 'user' ? '#1D9E75' : '#f5f5f5',
                      color: m.role === 'user' ? 'white' : '#1a1a1a',
                      fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                    }}>
                      {m.content}
                    </div>
                    {m.ticket && (
                      <div style={{
                        marginTop: 6, background: '#E1F5EE', border: '0.5px solid #5DCAA5',
                        borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#085041',
                      }}>
                        ✓ Ticket {m.ticket.id} created · confirmation email sent
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: '#999', marginTop: 3, textAlign: m.role === 'user' ? 'right' : 'left' }}>
                      {m.time}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div style={{ alignSelf: 'flex-start' }}>
                    <div style={{
                      background: '#f5f5f5', borderRadius: 12, borderBottomLeftRadius: 4,
                      padding: '10px 14px', display: 'flex', gap: 4,
                    }}>
                      {[0, 0.2, 0.4].map((d, i) => (
                        <span key={i} style={{
                          width: 6, height: 6, background: '#999', borderRadius: '50%',
                          display: 'inline-block',
                          animation: `bounce 1.2s ${d}s infinite`,
                        }} />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={messagesEnd} />
              </div>

              {/* Quick replies */}
              {messages.length <= 1 && (
                <div style={{
                  padding: '8px 12px', display: 'flex', gap: 6, flexWrap: 'wrap',
                  borderTop: '0.5px solid #f0f0f0', background: '#fafafa',
                }}>
                  {QUICK_REPLIES.map(q => (
                    <button
                      key={q}
                      onClick={() => handleSend(q)}
                      style={{
                        fontSize: 12, padding: '5px 10px', borderRadius: 12,
                        border: q === 'Book a Demo' ? '0.5px solid #0F6E56' : '0.5px solid #ddd',
                        background: q === 'Book a Demo' ? '#E1F5EE' : 'white',
                        color: q === 'Book a Demo' ? '#0F6E56' : '#444',
                        cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: q === 'Book a Demo' ? 600 : 400,
                      }}
                    >{q}</button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div style={{
                padding: '10px 12px', display: 'flex', gap: 8,
                alignItems: 'flex-end', borderTop: '0.5px solid #f0f0f0',
              }}>
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe your issue or ask a question..."
                  rows={1}
                  style={{
                    flex: 1, border: '0.5px solid #ddd', borderRadius: 18,
                    padding: '8px 14px', fontSize: 13, resize: 'none',
                    fontFamily: 'inherit', background: '#fafafa', outline: 'none', maxHeight: 90,
                  }}
                />
                <button
                  onClick={() => handleSend()}
                  disabled={loading}
                  style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    background: loading ? '#ccc' : '#1D9E75', border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                    <path d="M22 2L11 13M22 2L15 22 11 13 2 9l20-7z" />
                  </svg>
                </button>
              </div>
            </>
          )}

          {/* Tickets */}
          {tab === 'tickets' && (
            <div style={{
              flex: 1, overflowY: 'auto', padding: 12,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {tickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#999', fontSize: 13 }}>
                  No tickets yet. Start a chat if you need help!
                </div>
              ) : tickets.map(t => {
                const ps = priorityStyle(t.priority);
                return (
                  <div key={t.id} style={{
                    padding: 12, border: '0.5px solid #e8e8e8',
                    borderRadius: 8, background: 'white',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>{t.subject}</div>
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 10,
                        background: ps.bg, color: ps.color, whiteSpace: 'nowrap',
                      }}>{ps.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#999' }}>{t.id} · {t.created}</div>
                    {t.summary && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{t.summary}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
